import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from '../../../ds/clsx';
import { Pill, Skeleton, type PillVariant } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { propertyKeys, uploadPublicRequest, usePublicUploadPage } from '../api';
import type { PublicPropertyGroup, PublicRequest, PublicUploadPage } from '../types';
import styles from './DeveloperUploadPage.module.css';

const ACCEPT = '.pdf,.dwg,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png,image/vnd.dwg';

function Frame({
  context,
  end,
  children,
}: {
  context: string;
  end?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.wordmark}>DAMOKLES</span>
        <span className={styles.context}>{context}</span>
        {end ? <span className={styles.validity}>{end}</span> : null}
      </header>
      <main className={styles.main}>
        <div className={styles.column}>{children}</div>
      </main>
    </div>
  );
}

function statusVariant(status: PublicRequest['status']): PillVariant {
  return status === 'delivered' ? 'success' : status === 'newly_requested' ? 'warning' : 'danger';
}

interface RequestRowProps {
  token: string;
  request: PublicRequest;
  onUploaded: (response: { page: PublicUploadPage }) => void;
}

function RequestRow({ token, request, onUploaded }: RequestRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputId = `upload-${request.id}`;

  async function onChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      const response = await uploadPublicRequest(token, request.id, file, {
        onProgress: (fraction) => setProgress(Math.min(99, Math.round(fraction * 100))),
      });
      setProgress(null);
      onUploaded(response);
    } catch (err) {
      setProgress(null);
      if (err instanceof ApiError) setError(err.fields['file'] ?? err.message);
      else setError(errorMessage(err));
    }
  }

  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <div className={styles.rowTitle}>{request.title}</div>
        {request.hint ? <div className={styles.rowHint}>{request.hint}</div> : null}
        {error ? (
          <div className={styles.rowError} role="alert">
            {error}
          </div>
        ) : null}
      </div>
      {progress !== null ? (
        <span
          className={styles.progress}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          aria-label={request.title}
        >
          <span className={styles.progressTrack}>
            <span className={styles.progressFill} style={{ width: `${progress}%` }} />
          </span>
          <span className={styles.progressPct}>{progress} %</span>
        </span>
      ) : (
        <Pill variant={statusVariant(request.status)}>{request.statusLabel}</Pill>
      )}
      {request.canUpload && progress === null ? (
        <>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={ACCEPT}
            className={styles.srOnly}
            aria-label={`${request.title} hochladen`}
            onChange={(event) => void onChange(event)}
          />
          <button
            type="button"
            className={clsx('pressable', styles.uploadButton)}
            onClick={() => inputRef.current?.click()}
          >
            Hochladen
          </button>
        </>
      ) : null}
    </div>
  );
}

function Group({
  token,
  group,
  onUploaded,
}: {
  token: string;
  group: PublicPropertyGroup;
  onUploaded: (response: { page: PublicUploadPage }) => void;
}) {
  return (
    <>
      <p className={styles.intro}>{group.intro}</p>
      {group.complete ? (
        <div className={styles.card}>
          <div className={styles.eyebrow}>Vielen Dank</div>
          <h2 className={styles.thanksTitle}>Alle Unterlagen sind da.</h2>
          <p className={styles.thanksText}>{group.intro}</p>
        </div>
      ) : (
        <div className={styles.list}>
          {group.requests.map((request) => (
            <RequestRow key={request.id} token={token} request={request} onUploaded={onUploaded} />
          ))}
        </div>
      )}
      <div className={styles.footer}>{group.footer}</div>
    </>
  );
}

/** `/u/:token` — After-Sales 05/06: the developer's upload page, public, without login. */
export function DeveloperUploadPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const page = usePublicUploadPage(token);

  const applyPage = (response: { page: PublicUploadPage }) => {
    queryClient.setQueryData(propertyKeys.publicPage(token), response.page);
  };

  if (page.isPending) {
    return (
      <Frame context="Unterlagen">
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width={220} height={22} />
          <Skeleton width="80%" height={14} />
          <Skeleton variant="block" height={280} />
        </div>
      </Frame>
    );
  }

  if (page.isError) {
    const status = page.error instanceof ApiError ? page.error.status : 0;
    const expired = status === 410;
    const missing = status === 404;
    return (
      <Frame context="Unterlagen">
        <div className={styles.card}>
          <div className={styles.eyebrow}>
            {expired ? 'Link abgelaufen' : missing ? 'Link ungültig' : 'Fehler'}
          </div>
          <h1 className={styles.thanksTitle}>
            {expired
              ? 'Der Link ist abgelaufen.'
              : missing
                ? 'Dieser Link ist nicht mehr gültig.'
                : 'Die Seite konnte nicht geladen werden.'}
          </h1>
          <p className={styles.thanksText}>
            {expired || missing
              ? 'Braucht der Vertrieb noch Unterlagen, bekommen Sie eine neue E-Mail mit einem gültigen Link. Bei Fragen wenden Sie sich an den Absender der E-Mail.'
              : errorMessage(page.error)}
          </p>
          {!expired && !missing ? (
            <button
              type="button"
              className={clsx('pressable', styles.uploadButton)}
              onClick={() => void page.refetch()}
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      </Frame>
    );
  }

  const data = page.data;
  const complete = data.complete;
  const extra = data.properties.filter((group) => group.propertyName !== data.propertyName);

  return (
    <Frame context={data.headline} end={data.validUntilLabel}>
      {complete ? (
        <div className={styles.card}>
          <div className={styles.eyebrow}>Vielen Dank</div>
          <h1 className={styles.thanksTitle}>Alle Unterlagen sind da.</h1>
          <p className={styles.thanksText}>{data.intro}</p>
        </div>
      ) : (
        <>
          <h1 className={styles.greeting}>{data.greeting}</h1>
          <p className={styles.intro}>{data.intro}</p>
          <div className={styles.list}>
            {data.requests.map((request) => (
              <RequestRow key={request.id} token={token} request={request} onUploaded={applyPage} />
            ))}
          </div>
          <div className={styles.footer}>{data.footer}</div>
        </>
      )}
      {extra.map((group) => (
        <section key={group.propertyName} className={styles.group} aria-label={group.headline}>
          <h2 className={styles.groupTitle}>{group.headline}</h2>
          <Group token={token} group={group} onUploaded={applyPage} />
        </section>
      ))}
    </Frame>
  );
}
